import React, { useMemo, useState } from 'react';
import {
  Users,
  ChevronRight,
  Loader2,
  RotateCcw,
  Building2,
  MapPin,
  Trash2,
} from 'lucide-react';
import { useSRMS } from '../../context/SRMSContext';
import { User } from '../../types';
import {
  branchesOfBankZone,
  isPlaceholderName,
  sameName,
  zonesOfBank,
} from '../../utils/hierarchy';
import { userInBranch } from '../../utils/branchMatch';

/**
 * "Choose an agent first."
 *
 * Recovery Logs, Follow-up Logs, PTP Commitments, Field Visits, Geo-tagged
 * Photos, Voice Notes and the Commission Engine all used to open by listing
 * every record in the business and then letting you look for the one agent you
 * cared about. On a screen that is slow; on the bill it is worse.
 *
 * This shows a count per agent and nothing else until you pick one. The counts
 * come from `countCollectionForAgent`, which asks Firestore HOW MANY rather
 * than for the rows -- one read per agent instead of one per record. Seeing
 * "RA-0048: 312 field visits" costs one read; loading those 312 costs 312, and
 * only happens once you ask.
 *
 * Bank, zone and branch narrow WHICH agents are listed, so a bank with sixty
 * agents does not put sixty rows in front of you.
 */

export interface LogScopeGateProps {
  /** The Firestore collection these counts come from. */
  collectionName: 'recoveries' | 'followups' | 'ptps' | 'visits' | 'photos' | 'voicenotes';
  /** Plain word for what is being counted, e.g. "field visits". */
  noun: string;
  /** The chosen agent, or null while nothing is chosen. */
  selectedAgent: User | null;
  onSelectAgent: (agent: User | null) => void;
}

export const LogScopeGate: React.FC<LogScopeGateProps> = ({
  collectionName,
  noun,
  selectedAgent,
  onSelectAgent,
}) => {
  const { users, banks, zones, branches, countCollectionFor, deleteAgentRecordsIn, currentUser } =
    useSRMS();

  const [bank, setBank] = useState('');
  const [zone, setZone] = useState('');
  const [branch, setBranch] = useState('');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [counting, setCounting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const bankOptions = useMemo(
    () =>
      Array.from(
        new Set(banks.map((b) => (b.name || '').trim()).filter((n) => !isPlaceholderName(n)))
      ).sort(),
    [banks]
  );

  const zoneOptions = useMemo(
    () =>
      bank
        ? Array.from(
            new Set(
              zonesOfBank(zones, bank)
                .map((z) => (z.name || '').trim())
                .filter((n) => !isPlaceholderName(n))
            )
          ).sort()
        : [],
    [zones, bank]
  );

  const branchOptions = useMemo(
    () =>
      bank
        ? Array.from(
            new Set(
              branchesOfBankZone(branches, bank, zone || undefined)
                .map((b) => (b.name || '').trim())
                .filter((n) => !isPlaceholderName(n))
            )
          ).sort()
        : [],
    [branches, bank, zone]
  );

  /** Agents inside the chosen bank / zone / branch. */
  const agentsInScope = useMemo(() => {
    return users.filter((u) => {
      if (u.role !== 'agent') return false;
      if (!u.active) return false;

      if (bank) {
        const theirBanks = u.banks && u.banks.length ? u.banks : [u.bank];
        if (!theirBanks.some((b) => sameName(b, bank))) return false;
      }
      if (zone && !(sameName(u.zone, zone) || sameName(u.zonalOffice, zone))) return false;
      // Branch id first, then name AND bank: two banks can both have a
      // branch called Bidkin. See src/utils/branchMatch.ts.
      if (branch && !userInBranch(u, branch, bank, branches)) return false;
      return true;
    });
  }, [users, bank, zone, branch, branches]);

  const loadCounts = async () => {
    setCounting(true);
    const next: Record<string, number> = {};
    try {
      for (const u of agentsInScope) {
        const key = u.agentId || u.id;
        if (!key) continue;
        next[key] = await countCollectionFor(collectionName, key);
      }
      setCounts(next);
    } finally {
      setCounting(false);
    }
  };

  const fieldCls =
    'bg-white border border-slate-200 rounded-xl px-3 py-2 text-slate-900 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400';

  /* An agent is chosen: show who, and how to go back. */
  if (selectedAgent) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-blue-50/70 border border-blue-100 rounded-2xl text-xs">
        <div className="flex items-center gap-2 text-blue-900">
          <Users className="w-4 h-4" />
          <span className="font-bold">
            Showing {noun} for {selectedAgent.name}
          </span>
          <span className="font-mono text-blue-700">
            {selectedAgent.agentId || selectedAgent.id}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Menu-wise permanent delete -- your line 57. Administrator only,
              and it says exactly what it will remove before it does it. */}
          {currentUser?.role === 'admin' && (
            <button
              type="button"
              disabled={deleting}
              onClick={async () => {
                const key = selectedAgent.agentId || selectedAgent.id;
                const known = counts[key];
                const howMany =
                  typeof known === 'number' && known >= 0 ? `${known.toLocaleString('en-IN')} ` : '';
                const ok = window.confirm(
                  `Permanently delete ${howMany}${noun} belonging to ${selectedAgent.name}?\n\n` +
                    'This removes them from the database. It cannot be undone, and no other ' +
                    "menu's records are touched."
                );
                if (!ok) return;
                setDeleting(true);
                try {
                  const res = await deleteAgentRecordsIn(
                    collectionName,
                    key,
                    selectedAgent.name
                  );
                  window.alert(res.message);
                  if (res.success) {
                    setCounts((prev) => ({ ...prev, [key]: 0 }));
                  }
                } finally {
                  setDeleting(false);
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50 rounded-lg font-bold transition"
              title={`Permanently delete this agent's ${noun}`}
            >
              {deleting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Trash2 className="w-3.5 h-3.5" />
              )}
              <span>{deleting ? 'Deleting…' : `Delete these ${noun}`}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => onSelectAgent(null)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-lg font-bold transition"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Choose a different agent</span>
          </button>
        </div>
      </div>
    );
  }

  /* Nothing chosen yet. */
  return (
    <div className="p-4 bg-white border border-slate-200 rounded-2xl space-y-4 text-xs shadow-xs">
      <div>
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-600" />
          <span>Choose an agent to see their {noun}</span>
        </h3>
        <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
          Nothing is loaded until you pick one. Narrow by bank, zone or branch
          first if the list is long. “Show counts” asks the database how many
          each agent has — one read per agent, rather than one per record.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="flex items-center gap-1.5 text-slate-600 font-bold mb-1">
            <Building2 className="w-3.5 h-3.5" />
            <span>Bank</span>
          </label>
          <select
            value={bank}
            onChange={(e) => {
              setBank(e.target.value);
              setZone('');
              setBranch('');
              setCounts({});
            }}
            className={`w-full ${fieldCls}`}
          >
            <option value="">Every bank</option>
            {bankOptions.map((b) => (
              <option key={`lsg-bank-${b}`} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="flex items-center gap-1.5 text-slate-600 font-bold mb-1">
            <MapPin className="w-3.5 h-3.5" />
            <span>Zonal office</span>
          </label>
          <select
            value={zone}
            disabled={!bank}
            onChange={(e) => {
              setZone(e.target.value);
              setBranch('');
              setCounts({});
            }}
            className={`w-full ${fieldCls}`}
          >
            <option value="">{bank ? 'Every zone' : 'Pick a bank first'}</option>
            {zoneOptions.map((z) => (
              <option key={`lsg-zone-${z}`} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-slate-600 font-bold mb-1">Branch</label>
          <select
            value={branch}
            disabled={!bank}
            onChange={(e) => {
              setBranch(e.target.value);
              setCounts({});
            }}
            className={`w-full ${fieldCls}`}
          >
            <option value="">{bank ? 'Every branch' : 'Pick a bank first'}</option>
            {branchOptions.map((b) => (
              <option key={`lsg-br-${b}`} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-slate-500">
          {agentsInScope.length} agent{agentsInScope.length === 1 ? '' : 's'} in this scope
        </span>
        <button
          type="button"
          onClick={loadCounts}
          disabled={counting || agentsInScope.length === 0}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-slate-700 rounded-xl font-bold border border-slate-200 transition"
        >
          {counting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Users className="w-3.5 h-3.5" />
          )}
          <span>{counting ? 'Counting…' : `Show ${noun} counts`}</span>
        </button>
      </div>

      <div className="max-h-72 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100">
        {agentsInScope.length === 0 ? (
          <div className="p-4 text-slate-400">
            No active agents in this scope.
          </div>
        ) : (
          agentsInScope.map((u) => {
            const key = u.agentId || u.id;
            const n = counts[key];
            return (
              <button
                key={`lsg-agent-${u.id || key}`}
                type="button"
                onClick={() => onSelectAgent(u)}
                className="w-full flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-blue-50/60 text-left transition"
              >
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{u.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    <span className="font-mono">{key}</span>
                    {u.branch ? ` · ${u.branch}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {n === undefined ? (
                    <span className="text-[11px] text-slate-400">—</span>
                  ) : n < 0 ? (
                    <span
                      className="text-[11px] text-amber-700"
                      title="The database refused the count. Opening the agent still works."
                    >
                      count unavailable
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full font-mono font-bold text-[11px] border border-slate-200">
                      {n.toLocaleString('en-IN')}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LogScopeGate;
